import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import type { SocialPlatform } from "@content-os/config";

export interface SocialAccount {
  id: string;
  platform: SocialPlatform;
  username: string | null;
  avatar: string | null;
  status: "ACTIVE" | "EXPIRED" | "ERROR" | "DISCONNECTED";
  lastVerifiedAt: string | null;
}

export function useAccounts(brandId: string) {
  return useQuery({
    queryKey: ["accounts", brandId],
    queryFn: () => apiFetch<SocialAccount[]>(`/brands/${brandId}/accounts`),
    enabled: !!brandId,
  });
}

export function useSyncAccounts(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ synced: number; disconnected: number }>(
        `/brands/${brandId}/accounts/sync`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", brandId] });
      qc.invalidateQueries({ queryKey: ["zernio-available", brandId] });
    },
  });
}

export function useDisconnectAccount(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/brands/${brandId}/accounts/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["accounts", brandId] }),
  });
}

export interface AvailableAccount {
  id: string;
  platform: SocialPlatform;
  username: string | null;
  avatar: string | null;
  profile: { id: string; name: string } | null;
}

export function useAvailableAccounts(brandId: string) {
  return useQuery({
    queryKey: ["zernio-available", brandId],
    queryFn: () =>
      apiFetch<{ available: AvailableAccount[] }>(`/brands/${brandId}/zernio/available`),
    enabled: !!brandId,
  });
}

export function useLinkAccount(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (zernioAccountId: string) =>
      apiFetch<{ account: SocialAccount }>(
        `/brands/${brandId}/accounts/link`,
        {
          method: "POST",
          body: JSON.stringify({ zernioAccountId }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", brandId] });
      qc.invalidateQueries({ queryKey: ["zernio-available", brandId] });
    },
  });
}
